import {
  DescriptorEntorno,
  ENTORNO_DESARROLLO,
  ENTORNO_LOCAL,
  ENTORNO_NO_IDENTIFICADO,
  ENTORNO_PREPROD,
  ENTORNO_PRODUCCION,
  entornoSlot,
} from './entorno.model';

/**
 * Resolución del entorno: función pura, sin Angular y sin `window`, para poder probarla entera.
 *
 * <h3>Por el ORIGEN, no por el build</h3>
 * `environment.production` no sirve para esto. `azure-pipelines-dev.yml` y `azure-pipelines-slot.yml`
 * compilan los dos `npm run build:staging`, así que desarrollo, los slots de preview y preprod salen
 * del **mismo bundle** y el flag vale lo mismo en los tres. Lo único que los separa en tiempo de
 * ejecución es de dónde se ha descargado la página.
 *
 * <h3>Los patrones CIERRAN por la derecha</h3>
 * Con el lookahead `(?=[:/]|$)`, igual que `ES_PRODUCCION` en `electron/main.js`. Sin él,
 * `https://erp.tcgfactory.com.otrodominio.es` pasaría por producción, y de los dos errores posibles
 * ese es el que hace daño: un origen ajeno presentándose como el de casa.
 */

/** Producción. El `https?` admite http para no depender de cómo termine el binding de IIS. */
const PRODUCCION = /^https?:\/\/erp\.tcgfactory\.com(?=[:/]|$)/i;

const DESARROLLO = /^https?:\/\/erpdev\.tcgfactory\.com(?=[:/]|$)/i;

/** Cualquier puerto: `ng serve` usa el 4200, pero un segundo chat levanta el suyo en otro. */
const LOCAL = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(?=[:/]|$)/i;

/**
 * Resuelve el entorno a partir del origen de la página y del override declarado en el documento.
 *
 * @param origen    `location.origin` — esquema, host y puerto, sin barra final.
 * @param override  Contenido del `<meta name="erp-entorno">`, o `null` si no está. Vacío = «resuelve
 *                  tú por el origen», que es lo que lleva el `index.html` del repo.
 */
export function resolverEntorno(origen: string, override: string | null): DescriptorEntorno {
  const declarado = interpretarOverride(override);
  if (declarado) return declarado;

  if (PRODUCCION.test(origen)) return ENTORNO_PRODUCCION;
  if (DESARROLLO.test(origen)) return ENTORNO_DESARROLLO;
  if (LOCAL.test(origen)) return ENTORNO_LOCAL;

  // Ni coincide ni se declara. NO es desarrollo: ver el comentario de cabecera de `entorno.model.ts`.
  return ENTORNO_NO_IDENTIFICADO;
}

/**
 * Traduce el override, que es lo que el pipeline del slot escribe en el `index.html` desplegado.
 * Gramática deliberadamente pobre —`preprod` o `slot:<nombre>`— para que un valor mal escrito no
 * pueda colar un entorno benigno por error.
 *
 * Devuelve `null` cuando no hay nada declarado, y el descriptor de «no identificado» cuando hay algo
 * que no entendemos: un override ilegible es exactamente el caso de no saber dónde estás.
 */
function interpretarOverride(override: string | null): DescriptorEntorno | null {
  const valor = override?.trim().toLowerCase() ?? '';
  if (!valor) return null;

  if (valor === 'produccion') return ENTORNO_PRODUCCION;
  if (valor === 'desarrollo') return ENTORNO_DESARROLLO;
  if (valor === 'local') return ENTORNO_LOCAL;
  if (valor === 'preprod') return ENTORNO_PREPROD;

  if (valor.startsWith('slot:')) {
    const nombre = valor.slice('slot:'.length).trim();
    return nombre ? entornoSlot(nombre) : ENTORNO_NO_IDENTIFICADO;
  }

  return ENTORNO_NO_IDENTIFICADO;
}
